import React, { useEffect, useState, useContext } from 'react';
import { connect } from 'react-redux';
import Box from '@material-ui/core/Box';
import Button from 'common/components/Button';
import TextField from '@material-ui/core/TextField';
import * as yup from 'yup';
import { shopCartOperations } from 'store/ducks/shopCart';
import { makeOutlinedInputClasses, makePricePanelStyles } from 'styles/pages/product';
import SvgIcon from 'common/components/SvgIcon';
import cardIcon from 'assets/icons/icon_cart.svg';
import { formatPrice } from 'common/functions';
import { StoreBasicDataApiStructure } from 'common/types';
import { repositories } from 'api';
import { NotificationStateContext } from 'common/components/NotificationProvider';
import { PendingData, } from 'store/ducks/pendingData/types';
import { getIsLoadingFlagAtAddingItemsToTheCartShop } from 'common/selectors';
import { getItemsAmountInCartShop } from "store/ducks/shopCart/selectors";
import { ItemsInShopAmountType, ShopCartItem } from "store/ducks/shopCart/types";

type Props = {
    addToShopCart: Function;
    code: string;
    images: any;
    isPromoter?: boolean;
    amountItemsInCartShop: ItemsInShopAmountType,
    minQuantity: number;
    name: string;
    originalPrice?: Price;
    price: Price;
    setQuantity: Function;
    slug: string;
    type: string;
    quantity: number | string | null;
    taxAmount: number;
    pendingData: {
        carts: boolean;
        cartItems: boolean;
    };
};

type Price = {
    current: number;
    currency: string;
};

const Error = ({ error }: { error: string }) => {
    const { warning } = makePricePanelStyles();

    return error ? <Box className={warning}>{error}</Box> : null;
};

const validation = (value: number | string | null, setError: Function, validQuantity: number) =>
    yup
        .number()
        .transform((currentValue: any, originalValue: any) => {
            return originalValue === '' || originalValue === null ? undefined : currentValue;
        })
        .required('Podaj liczbę egzemplarzy.')
        .min(validQuantity, `Minimalna liczba egzemplarzy: ${validQuantity}.`)
        .validate(value)
        .then(() => setError(''))
        .catch((error) => setError(error?.errors));

const notificationBaseConfig = {
    open: true,
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
    },
};

const notificationConfig = {
    success: {
        ...notificationBaseConfig,
        text: 'Produkt został dodany do koszyka',
    },

    error: {
        ...notificationBaseConfig,
        severity: 'error',
    },
};

const PricePanel: React.FC<Props> = ({
    addToShopCart,
    code,
    images,
    isPromoter,
    amountItemsInCartShop,
    minQuantity,
    name,
    price,
    setQuantity,
    slug,
    type,
    quantity,
    taxAmount,
    pendingData
}) => {
    const [error, setError] = useState('');

    const { iconWrapper, input, priceBox } = makePricePanelStyles();
    const notification = useContext(NotificationStateContext);
    const outlinedInputClasses = makeOutlinedInputClasses();
    const priceFormatted = price && formatPrice(price.current);
    const amountInCartShop = amountItemsInCartShop[code];

    const validQuantity = isPromoter ? 1 : minQuantity;

    const invokeAddToShopCart = () => {
        const showNotification = (data?: { error?: boolean; messages?: Array<string> }) => {
            const config = data?.error ? 'error' : 'success';

            const finalConfig = data?.error
                ? { ...notificationConfig[config], text: data?.messages || 'Błąd zamówienia' }
                : notificationConfig[config];

            // @ts-ignore
            notification.setConfig(finalConfig);
        };

        const product = {
            name,
            code,
            slug,
            price,
            type,
            images,
            minQuantity,
            taxAmount,
        };

        addToShopCart(product, Number(quantity), showNotification);
    };

    const handleChange = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setQuantity(value);
        validation(value, setError, validQuantity);
    };

    useEffect(() => {
        if (quantity === null) {
            setQuantity(validQuantity);
            return;
        }

        validation(quantity, setError, validQuantity);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            <Box display="flex" flex={1} justifyContent="flex-end" position="relative">
                <Box display="inline-flex">
                    <TextField
                        name="price"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            classes: outlinedInputClasses,
                        }}
                        inputProps={{
                            className: input,
                            min: validQuantity,
                            onChange: handleChange,
                        }}
                        label="egz."
                        type="number"
                        value={quantity ?? ''}
                        variant="outlined"
                    />
                    <Error error={error} />
                    <Button
                        disabled={error.length >= 1 || pendingData.cartItems || pendingData.carts}
                        onClick={invokeAddToShopCart}
                        size="medium"
                        type="button"
                    >
                        <Box className={iconWrapper}>
                            <SvgIcon height={20} src={cardIcon} />
                        </Box>
                        Dodaj do koszyka
                    </Button>
                    <Box className={priceBox}>{priceFormatted}</Box>
                </Box>

            </Box>
            {amountInCartShop > 0 && <Box mt={2}
                                          width={{ md: '100%', }}
                                          color="warning.main"
                                          textAlign={{ md: 'right', lg: 'left', }}
                                          pl={{ lg: '95px', }}>
                {amountInCartShop} egz. w koszyku
            </Box>}
        </Box>
    );
};

type MappingType = {
    me: StoreBasicDataApiStructure<repositories.MeType>,
    pendingData: PendingData,
    shopCart: Array<ShopCartItem>,
};

const mapStateToProps = ({ me, pendingData, shopCart }: MappingType) => {
    const { isPromoter } = me.data || {};

    return {
        isPromoter,
        pendingData: getIsLoadingFlagAtAddingItemsToTheCartShop(pendingData),
        amountItemsInCartShop: getItemsAmountInCartShop({shopCart}),
    };
};

const mapDispatchToProps = {
    addToShopCart: shopCartOperations.addToShopCart,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricePanel);
